/**
 * Test ID utilities for QA automation
 *
 * Schema:
 * - Radio buttons: {question_slug}_radio-{yes|no|value}
 * - Checkboxes: {question_slug}_checkbox-{index}
 * - Text inputs: {question_slug}_input
 * - Textareas: {question_slug}_textarea
 * - Continue buttons: {question_slug}-continue
 */

// Map question IDs to human-readable slugs
// Add new questions here as they are identified
const QUESTION_ID_TO_SLUG: Record<string, string> = {
  // Custom/pre-questions
  "medication-check": "medical_conditions",

  // =====================================================
  // MEN ENGLISH QUESTIONNAIRE (9bd5bdc8-b10d-497b-b3d3-0ac69464de3d)
  // =====================================================
  "947055a6-af8f-4082-ba4b-2607d796bbbc": "men_en_right_place",
  "40c3c872-4a06-4181-bacf-ef0b97f5c040": "men_en_wrong_place",
  "8552b567-3a20-4be9-b7ec-4612fca7bbf2": "men_en_how_long_problem",
  "91089743-a209-49d6-b6a6-d5a19e6a921a": "men_en_how_concerned",
  "073fc214-4eb4-4852-9316-63cbab7195ba": "men_en_meds_tried",
  "a5342ed2-f09d-4474-8356-5a41c4adb5e8": "men_en_viagra_help",
  "384ba69f-bf63-4985-8997-c115113037a1": "men_en_cialis_help",
  "5dda3e84-8763-4f8a-9431-f117b73fad9a": "men_en_levitra_help",
  "3c9ecea0-c420-489c-92ec-2f9da6f8deda": "men_en_device_help",
  "b1300638-1608-4f37-ba44-85e4d1d19f63": "men_en_other_tried",
  "03512ffd-d5c1-4271-966f-0f5793fc7865": "men_en_apply_to_you",
  "bb8e5d8a-48cc-4b67-a290-3fa2638aafc8": "men_en_conditions",
  "76ae67de-4396-4bc4-8c57-a07f18eb84c2": "men_en_pulmonary_hypertension_confirm",
  "5c3a7dfa-7100-494f-93f4-2c4671b7b204": "men_en_heart_attack_surgery",
  "aec13292-210b-45e7-8ba1-07c9b6e94b14": "men_en_heart_attack_details",
  "317cc7e9-82cf-4b74-9636-518b98263eaa": "men_en_genital_abnormalities",
  "27826013-3d73-41d1-9f92-23124ebf690a": "men_en_kidney_history",
  "94a2e264-44bf-40e1-a86f-b5a18c5f02a6": "men_en_kidney_labs",
  "6b989660-5e64-441d-97d1-43d100a16c3f": "men_en_upload_labs",
  "3fd3203b-6315-42c3-a7c0-25b26c5756cd": "men_en_lab_details",
  "bb2682ca-943b-4bbd-9f45-159d4a72701f": "men_en_kidney_specialist",
  "c1d1c177-8edd-4d77-b3bf-29fe3d7becee": "men_en_recreational_drugs",
  "5d96fda7-15b5-4286-8fa4-445430cc4202": "men_en_medications",
  "880857ae-110a-4161-aaaa-2f91a3ee86ce": "men_en_nitrates",
  "b4eaebe1-abda-423b-84da-1e82b6dc0c5d": "men_en_nitrates_details",
  "5ff4b5d3-7b63-4a6c-98ed-6979ef58cd2a": "men_en_vitals_tested",
  "8f797bde-71f1-4bff-92ba-06f9447919ee": "men_en_blood_pressure",
  "8310421f-ae56-4eef-b8f5-563b5a8d841c": "men_en_message_to_doctor",

  // =====================================================
  // MEN SPANISH QUESTIONNAIRE (a76432d1-17aa-4d4e-b9d9-2e64f26d5d71)
  // =====================================================
  "8649be7e-b097-4494-ad1b-9577917bb112": "men_es_right_place",
  "4e823493-2b6d-4c68-bfad-63f065e63187": "men_es_wrong_place",
  "a4917f29-37e9-4b65-bcda-0499bec00c11": "men_es_how_long_problem",
  "8b5ae014-936a-48be-b206-0916964de47d": "men_es_how_concerned",
  "f9de98f5-3e3a-4c86-ad3e-1ac0b5505e5e": "men_es_meds_tried",
  "94a27dc8-afd6-4c1c-b783-8c76fe08fb6c": "men_es_viagra_help",
  "5fde9357-20aa-4c88-9329-0bd8ee3b6fd5": "men_es_cialis_help",
  "f91e9d6b-562b-4593-95bd-f5da1c6400d1": "men_es_levitra_help",
  "5baf916d-434b-484c-b83d-1419909674f2": "men_es_device_help",
  "a7b5d146-da23-47a1-a1ed-e3c5b1b49c42": "men_es_other_tried",
  "872f3c52-44cf-4485-b413-5b416ea00fe7": "men_es_apply_to_you",
  "c83a40f0-b528-44a7-949f-22b86a4bebbd": "men_es_conditions",
  "6cf1c73e-a0fc-423a-b56a-96c64480a5f7": "men_es_pulmonary_hypertension_confirm",
  "2dea3f90-48c7-4676-b147-52c94d8f1c7f": "men_es_heart_attack_surgery",
  "a4d0f3ff-8fe1-4793-bf35-629dc905453a": "men_es_heart_attack_details",
  "ad9b6b54-c64d-4c01-b298-e066bfba834b": "men_es_genital_abnormalities",
  "fcee0985-7d23-48e0-a202-7a0133970999": "men_es_kidney_history",
  "c6f948c5-cc1c-4cd5-803d-ecd93445145e": "men_es_kidney_labs",
  "a9cf49cd-c132-4990-bc11-8137721d0a06": "men_es_upload_labs",
  "0908f0a5-fb33-4c05-af05-8667f85601f5": "men_es_lab_details",
  "e8969ee7-70c6-4cc9-9bd7-ab913dd3d799": "men_es_kidney_specialist",
  "0875937a-b330-4845-80d8-caa795249014": "men_es_recreational_drugs",
  "3157ba74-dfa3-4ad8-a582-6876748ca5e6": "men_es_medications",
  "8158c62a-aa8c-48f3-b675-4202c07f70fc": "men_es_nitrates",
  "b3914874-a776-4899-9d15-cffd533184e4": "men_es_nitrates_details",
  "1b29d73d-b3ce-4f70-82b3-57b6f9a7eab3": "men_es_vitals_tested",
  "abc54902-af7a-4de8-8881-0be0a4402989": "men_es_blood_pressure",
  "afcd2c8b-d802-4a0a-8e4d-24c952c4510d": "men_es_message_to_doctor",

  // =====================================================
  // WOMEN ENGLISH QUESTIONNAIRE (8513a415-0d1c-4d32-9892-45f2b35b2963)
  // =====================================================
  "1dbeb531-0bde-487e-a8a6-ff667cd75f2b": "women_en_psychiatric_meds",
  "40aafa3b-0b34-419f-9390-0a9363d48c60": "women_en_psychiatric_meds_list",
  "be7f26db-a7fb-485e-8c16-564a11916e0a": "women_en_opiate_meds",
  "30c3f65c-6c0e-4bf2-a0e5-3e54a0e4ca94": "women_en_opiate_meds_list",
  "8818e568-1250-4e62-bfa9-33d860cea39b": "women_en_postmenopausal",
  "39b2e871-7baf-4f44-b39e-d8cea553fa9d": "women_en_conditions_checklist",
  "29de0841-2392-4a85-86fe-4797a197ca83": "women_en_not_ideal_candidate",
  "9d8a6285-5691-42dc-8a8d-41e50bb6f62b": "women_en_pregnant_breastfeeding",
  "bddbf989-d90e-47bb-9915-17205bc500ce": "women_en_pregnancy_unsafe",
  "420b6a60-8d31-4bf7-8d6c-ee25b54e6de5": "women_en_medical_conditions",
  "801b7cae-8c59-4dee-8083-fc110f75fa40": "women_en_medical_conditions_unsafe",
  "5ab40f7a-d064-4aa0-9835-e1c663447f60": "women_en_nausea_motion_sickness",
  "57ec4e4c-0c9a-45dd-a5b7-4786ad3cfaed": "women_en_blood_pressure_meds",
  "f3e400d2-5860-424d-96e9-4535710fd397": "women_en_meds_not_ideal_candidate",
  "c82b3d06-8e45-4d67-bccc-fcbb87b6aeed": "women_en_last_medical_eval",
  "ff7b8c52-4d6e-4759-8a74-604d5a685c06": "women_en_info_pt141",
  "4331230b-a18a-4164-9a2f-fcb271b3d4fd": "women_en_message_to_doctor",

  // =====================================================
  // WOMEN SPANISH QUESTIONNAIRE (fead44f6-0f4f-4e9c-9b02-77e2f55f5c12)
  // =====================================================
  "f0ede954-8325-4dce-8451-5cb0067edf64": "women_es_psychiatric_meds",
  "4ca40f9b-dbed-4db5-a834-4a08e13bc364": "women_es_psychiatric_meds_list",
  "24a09a8d-1459-4cdf-9bb9-a82ca47afa27": "women_es_opiate_meds",
  "5e094dae-95a0-4876-875e-a460103fa56a": "women_es_opiate_meds_list",
  "bf57d8ee-326c-4d2a-8a83-aaf815133176": "women_es_postmenopausal",
  "2f27d04f-01b0-4754-ae98-d75273bc55f6": "women_es_conditions_checklist",
  "35a94891-8e9b-4b55-bf42-b40125dbc217": "women_es_not_ideal_candidate",
  "6aa8de78-5af0-480a-938a-0d48cd6cfb65": "women_es_pregnant_breastfeeding",
  "57351939-e553-45cf-b3e3-7a42f3f4da5b": "women_es_pregnancy_unsafe",
  "65b18e2f-57c0-4413-ba3d-6b1884a260d2": "women_es_medical_conditions",
  "edbdfff4-92a4-4aad-904f-1cea92bd5298": "women_es_medical_conditions_unsafe",
  "9d65214d-4661-45cf-9799-bdbb68d23328": "women_es_nausea_motion_sickness",
  "822d5612-60bf-450b-9764-e853c1c0cf51": "women_es_blood_pressure_meds",
  "3b2ec02c-cc85-42f5-882e-78a0433ddb82": "women_es_meds_not_ideal_candidate",
  "a39dacbe-a8bd-4657-a463-248eafcea131": "women_es_last_medical_eval",
  "2f02cfe6-8365-4c7c-9a62-0958759924c6": "women_es_info_pt141",
  "113587f0-3bdf-48a2-abb4-728322c3519c": "women_es_message_to_doctor",
};

/**
 * Get the slug for a question ID
 * Returns undefined if no mapping exists (test ID will not be added)
 */
export function getQuestionSlug(questionId: string): string | undefined {
  return QUESTION_ID_TO_SLUG[questionId];
}

/**
 * Generate test ID for a radio button
 * @param questionId - The partner_questionnaire_question_id
 * @param optionValue - The option value (e.g., "Yes", "No")
 */
export function getRadioTestId(questionId: string, optionValue: string): string | undefined {
  const slug = getQuestionSlug(questionId);
  if (!slug) return undefined;

  // Normalize option value to lowercase
  const normalizedValue = optionValue.toLowerCase();
  return `${slug}_radio-${normalizedValue}`;
}

/**
 * Generate test ID for a checkbox
 * @param questionId - The partner_questionnaire_question_id
 * @param index - The 1-based index of the checkbox
 */
export function getCheckboxTestId(questionId: string, index: number): string | undefined {
  const slug = getQuestionSlug(questionId);
  if (!slug) return undefined;

  return `${slug}_checkbox-${index}`;
}

/**
 * Generate test ID for a text input
 * @param questionId - The partner_questionnaire_question_id
 */
export function getInputTestId(questionId: string): string | undefined {
  const slug = getQuestionSlug(questionId);
  if (!slug) return undefined;

  return `${slug}_input`;
}

/**
 * Generate test ID for a textarea
 * @param questionId - The partner_questionnaire_question_id
 */
export function getTextareaTestId(questionId: string): string | undefined {
  const slug = getQuestionSlug(questionId);
  if (!slug) return undefined;

  return `${slug}_textarea`;
}

/**
 * Generate test ID for a continue button
 * @param questionId - The partner_questionnaire_question_id
 */
export function getContinueButtonTestId(questionId: string): string | undefined {
  const slug = getQuestionSlug(questionId);
  if (!slug) return undefined;

  return `${slug}-continue`;
}

/**
 * Generate test IDs for rejection (cul-de-sac) action buttons.
 * Falls back to a slug-based convention for any mapped question ID.
 */
export function getRejectionBackButtonTestId(questionId: string): string | undefined {
  const slug = getQuestionSlug(questionId);
  if (!slug) return undefined;

  return slug.includes("wrong_place")
    ? slug.replace("wrong_place", "rejection_back_button")
    : `${slug}_rejection_back_button`;
}

export function getRejectionCloseButtonTestId(questionId: string): string | undefined {
  const slug = getQuestionSlug(questionId);
  if (!slug) return undefined;

  return slug.includes("wrong_place")
    ? slug.replace("wrong_place", "rejection_close_button")
    : `${slug}_rejection_close_button`;
}
